
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { complianceService } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import CertificateList from './components/Certificates/CertificateList';
import { HealthScoreWidget } from './components/HealthScoreWidget';
import { EvidenceVault } from './components/Evidence/EvidenceVault';
import RequestAcknowledgementModal from './components/RequestAcknowledgementModal';
import { NLPAnalysisPanel } from './components/NLPAnalysisPanel';
import { calculateComplianceScore, calculateHealthScore, checkCriticalCertificates } from '../../utils/complianceCalculator';
import { useSystemFlag } from '../../config/useSystemFlags';
import { AcknowledgementList } from './components/Acknowledgements/AcknowledgementList';
import { CriticalBreachModal } from './components/CriticalBreachModal';
import EditPropertyModal from './components/EditPropertyModal';
import AddTenantModal from '../Tenants/AddTenantModal';
import EditTenantModal from '../Tenants/EditTenantModal';
import { tenantService } from '../../services/api';
import CSVImportModal from '../Shared/CSVImportModal';
import OrderServiceModal from './OrderServiceModal';
import CommsHistory from './components/CommsHistory';
import ReceiptsList from './components/ReceiptsList';
import { getAppMarket } from '../../services/MarketService';
import { categoryLabel } from './PropertyForm';
import { formatPropertyLine1, formatPropertyPostcode } from '../../utils/formatAddress';
import {
    RentalRegistrationCard,
    RekeyStatusCard,
    HvacReadinessCard,
    TexasDocumentValidation,
    TexasPropertyInsights,
} from './components/TexasPropertySections';

interface PropertyDetailProps {
    propertyId?: string | null;
    isDrawer?: boolean;
    onClose?: () => void;
}

export default function PropertyDetail({ propertyId, isDrawer, onClose }: PropertyDetailProps = {}) {
    const params = useParams<{ id: string }>();
    const id = propertyId || params.id;
    const navigate = useNavigate();
    const [property, setProperty] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Open the Request Ack modal automatically when the page is loaded
    // with `#request-acknowledgement` in the URL — the Tenancy Wizard
    // sends officers here after creating a pending-activation tenancy.
    // F-308: the acknowledgement flow is UK-only, never auto-open in Texas.
    const ackHash = () =>
        getAppMarket() !== 'texas' && window.location.hash === '#request-acknowledgement';
    const [showAckModal, setShowAckModal] = useState(
        typeof window !== 'undefined' && ackHash()
    );
    useEffect(() => {
        const handler = () => {
            if (ackHash()) setShowAckModal(true);
        };
        window.addEventListener('hashchange', handler);
        return () => window.removeEventListener('hashchange', handler);
    }, []);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    // Preserving refresh trigger from legacy code just in case, though new modal uses onSuccess={fetchProperty}
    const [ackRefreshTrigger, setAckRefreshTrigger] = useState(0);
    const [showBreachModal, setShowBreachModal] = useState(false);
    const [tenants, setTenants] = useState<any[]>([]);
    const [showAddTenantModal, setShowAddTenantModal] = useState(false);
    const [showEditTenantModal, setShowEditTenantModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
    const prsRegistrationVisible = useSystemFlag('prsRegistration');
    const isTexasMarket = getAppMarket() === 'texas';
    const [totalRent, setTotalRent] = useState(0);

    const fetchProperty = async () => {
        if (!id) {
            setError("No property ID provided");
            // setLoading(false); // Handled by fetchData
            return;
        }
        console.log('[PropertyDetail] Fetching property:', id);
        // setLoading(true); // Handled by fetchData
        setError(null);
        try {
            const res = await complianceService.getProperty(id);
            console.log('[PropertyDetail] API response received', res.status);
            
            if (!res.data || (typeof res.data !== 'object')) {
                throw new Error("Invalid response format from property service");
            }

            const propertyData = res.data.data || res.data;
            
            if (!propertyData || typeof propertyData !== 'object') {
                throw new Error("No property data found in response");
            }

            console.log('[PropertyDetail] Property data received:', {
                certificates: propertyData.certificates,
                certificateCount: propertyData.certificates?.length || 0
            });

            // Calculate compliance score from certificates
            try {
                const hasGas = propertyData.hasGasInstallation !== false;
                // Attach for nested checks in healhScore
                if (propertyData.certificates) {
                    (propertyData.certificates as any).hasGasInstallation = hasGas;
                }

                const score = calculateComplianceScore(propertyData.certificates || [], hasGas);
                propertyData.complianceScore = score;

                // F-015: Calculate Health Score
                // Passing null for analysisResults for now (will implement async fetch later)
                const health = calculateHealthScore(propertyData.certificates || [], null, {
                    prsRegistrationNumber: propertyData.prsRegistrationNumber,
                    prsExpiryDate: propertyData.prsExpiryDate
                });
                propertyData.health = health;

                // Check if ALL critical documents are present and valid
                const criticalCheck = checkCriticalCertificates(propertyData.certificates || [], hasGas);
                propertyData.hasAllCriticalDocs = criticalCheck.hasAllCritical;
                propertyData.missingCriticalDocs = criticalCheck.missingTypes;

                console.log('[PropertyDetail] Calculated scores:', { compliance: score, health, criticalCheck, hasGas });
            } catch (scoreError) {
                console.error('[PropertyDetail] Score calculation failed:', scoreError);
                propertyData.complianceScore = 0;
            }

            setProperty(propertyData);
            // PROD HOTFIX: Disable automatic pop-up as per user request
            // if (propertyData.missingCriticalDocs?.length > 0) {
            //     setShowBreachModal(true);
            // }
            setError(null);
        } catch (err: any) {
            console.error("[PropertyDetail] Failed to fetch property", {
                error: err,
                message: err?.message,
                response: err?.response
            });
            setError(err?.response?.data?.message || "Property not found or access denied.");
            // Explicitly ensuring no re-throw occurs here
        } finally {
            // setLoading(false); // Handled by fetchData
        }
    };

    const fetchTenants = async () => {
        if (!id) return;
        try {
            const res = await tenantService.getTenantsByProperty(id);
            // Robust guard for data access
            const tenantsList = (res.data?.success ? res.data.data : res.data?.data) || res.data || [];
            const safeTenantsList = Array.isArray(tenantsList) ? tenantsList : [];
            setTenants(safeTenantsList);

            // F-025: Sum ACTIVE tenant rents only. An ended tenancy no longer
            // contributes rent to the property — mirror the backend filter at
            // routes/landlord.js:550-554.
            const sum = safeTenantsList.filter(isActiveTenancy)
                .reduce((acc: number, t: any) => acc + (Number(t?.rentAmount) || 0), 0);
            setTotalRent(sum);
        } catch (err) {
            console.error('[PropertyDetail] Failed to fetch tenants', err);
        }
    };

    // A tenancy is "ended" if its status is explicitly ended/former/inactive,
    // OR the tenancyEndDate has passed. Matches the backend definition.
    const isActiveTenancy = (t: any): boolean => {
        const status = (t?.status || '').toString().toLowerCase();
        if (status === 'ended' || status === 'former' || status === 'inactive') return false;
        if (t?.tenancyEndDate && new Date(t.tenancyEndDate) <= new Date()) return false;
        return true;
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            await Promise.all([fetchProperty(), fetchTenants()]);
        } catch (err) {
            // Error handling for any of the promises
            console.error('[PropertyDetail] Error during fetchData:', err);
            // The individual fetch functions already set specific errors,
            // but this catch can be used for a general failure message if needed.
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // ID format check was too restrictive for some legacy or test data.
        // We now just check for existence and call fetchData.
        if (id) {
            console.log('[PropertyDetail] Validating ID:', id);
            fetchData();
        } else {
            console.warn('[PropertyDetail] Missing ID in route or props');
            setLoading(false);
            setError("Missing Property Identifier.");
        }
    }, [id, navigate]);

    // Loading state
    if (loading) return (
        <div className={`${isDrawer ? '' : 'min-h-screen'} bg-gray-50 flex items-center justify-center`}>
            <div className="animate-pulse flex flex-col items-center">
                <div className="h-12 w-12 bg-green-200 rounded-full mb-4"></div>
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
            </div>
        </div>
    );



    // Data error state  
    if (error || (!property && !loading)) return (
        <div className={`${isDrawer ? '' : 'min-h-screen'} bg-gray-50 flex flex-col`}>
            {!isDrawer && <Navbar />}
            <div className="flex-1 flex flex-col items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-sm">
                    <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                    <h2 className="text-lg font-bold text-gray-900 mb-2">Error Loading Property</h2>
                    <p className="text-sm text-gray-600 mb-6">{error || "Property data unavailable."}</p>
                    {isDrawer ? (
                        <button
                            onClick={() => onClose?.()}
                            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition text-sm font-bold uppercase tracking-wider"
                        >
                            Return to Dashboard
                        </button>
                    ) : (
                        <button
                            onClick={() => navigate('/properties')}
                            className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition font-bold"
                        >
                            Back to Portfolio
                        </button>
                    )}
                </div>
            </div>
            {!isDrawer && <Footer />}
        </div>
    );

    // Guard against null/undefined property
    if (!property) return null;

        return (
            <div className={`bg-gray-50 ${isDrawer ? '' : 'min-h-screen'} font-sans flex flex-col`}>
                {!isDrawer && <Navbar />}

                <div className={`flex-1 ${isDrawer ? 'px-4' : 'max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8'} py-8`}>

                    {/* Header / Breadcrumbs + Close Button for Drawer */}
                    {isDrawer && (
                        <div className="mb-6 flex justify-between items-center">
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-900">
                                    {typeof property?.address === 'object' ? (property?.address?.line1 || 'Property Details') : (property?.address || 'Property Details')}
                                </h3>
                                <button
                                    onClick={() => navigate(`/properties/${id}`)}
                                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium mt-1 flex items-center gap-1"
                                >
                                    <i className="fas fa-expand"></i>
                                    View Full Details
                                </button>
                            </div>
                            <button
                                onClick={() => onClose?.()}
                                className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
                                title="Close property details"
                            >
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>
                    )}
                    {!isDrawer && (
                    <div className={isTexasMarket ? 'mb-3' : 'mb-8'}>
                        <nav className="flex mb-4" aria-label="Breadcrumb">
                            <ol className="flex items-center space-x-4">
                                <li>
                                    <div>
                                        <a href="/properties" className="text-gray-400 hover:text-gray-500">
                                            <i className="fas fa-home flex-shrink-0 h-5 w-5"></i>
                                            <span className="sr-only">Home</span>
                                        </a>
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-center">
                                        <i className="fas fa-chevron-right text-gray-300 flex-shrink-0 h-5 w-5" aria-hidden="true"></i>
                                        <a href="/properties" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">Properties</a>
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-center">
                                        <i className="fas fa-chevron-right text-gray-300 flex-shrink-0 h-5 w-5" aria-hidden="true"></i>
                                        {/* F-309: on the Texas market the address
                                            segment reads as the page title — bold
                                            and larger than the breadcrumb links.
                                            UK keeps the original style. */}
                                        <span aria-current="page" className={`ml-4 ${isTexasMarket ? 'text-lg font-bold text-gray-900' : 'text-sm font-medium text-gray-500'}`}>{typeof property?.address === 'object' ? (property?.address?.line1 || 'Property') : (property?.address || 'Property')}</span>
                                    </div>
                                </li>
                            </ol>
                        </nav>

                        {/* F-309: Texas drops the duplicate address heading +
                            subtitle ("Texas - Residential") below the breadcrumb
                            — the breadcrumb address is the title now (item 1) —
                            and the block's bottom padding is tightened so the
                            Texas Insights panel moves up. UK unchanged, and the
                            drawer variant keeps the heading (it has no
                            breadcrumb to carry the address). */}
                        {(!isTexasMarket || isDrawer) && (
                        <div className="md:flex md:items-center md:justify-between">
                            <div className="flex-1 min-w-0">
                                {/* F-307: bolder address heading; the pencil edit
                                    affordance removed (Edit Property lives on
                                    the Property Details card). */}
                                <div className="flex items-center">
                                    <h2 className="text-2xl font-black tracking-tight leading-7 text-gray-900 sm:text-3xl sm:truncate">
                                        {formatPropertyLine1(property, 'Property Details')}
                                    </h2>
                                </div>
                                <p className="mt-1 text-sm text-gray-500">
                                    {formatPropertyPostcode(property)} • {property?.type || 'Property'}
                                </p>
                            </div>
                            {/* F-306: Texas market drops the UK-oriented action
                                cluster (Order Certificate / Request
                                Acknowledgement / Compliant badge). */}
                            {!isTexasMarket && (
                            <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
                                <button
                                    onClick={() => navigate(`/certificate-orders/new?propertyId=${id}`)}
                                    className="inline-flex items-center px-4 py-2 border border-emerald-500 text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                                >
                                    <i className="fas fa-certificate mr-2"></i>
                                    Order Certificate
                                </button>
                                <button
                                    onClick={() => setShowAckModal(true)}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                    <i className="fas fa-envelope mr-2"></i>
                                    Request Acknowledgement
                                </button>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${property?.hasAllCriticalDocs ? 'bg-green-100 text-green-800' : 'bg-red-600 text-white'}`}>
                                    {property?.hasAllCriticalDocs ? <><i className="fas fa-shield-alt mr-2"></i>Compliant</> : <><i className="fas fa-exclamation-triangle mr-2"></i>Action Required</>}
                                </span>
                            </div>
                            )}
                        </div>
                        )}
                    </div>
                    )}

                    {/* F-025: Compliance Alert Banners (Missing component above Litigation Readiness) */}
                    <div className="space-y-4 mb-8">
                        {/* PROD HOTFIX: Removed Level 3 Red Alert banner as per user request */}

                        {tenants.some(t => (t.arrears || 0) > 0) && (
                            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg shadow-sm">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <i className="fas fa-hand-holding-usd text-orange-500"></i>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-bold text-orange-800">
                                            Arrears Detected
                                        </h3>
                                        <div className="mt-2 text-sm text-orange-700">
                                            <p className="font-bold">Total Arrears: {isTexasMarket ? '$' : '£'}{tenants.reduce((acc, t) => acc + (t.arrears || 0), 0).toFixed(2)}</p>
                                            <p className="mt-1 font-medium">Automatic alerts have been prepared. Please review the financial ledger.</p>
                                        </div>
                                        <div className="mt-4">
                                            <button
                                                onClick={() => navigate('/financials')}
                                                className="bg-orange-600 text-white px-3 py-1.5 rounded-md text-xs font-bold hover:bg-orange-700 transition"
                                            >
                                                VIEW LEDGER
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Syntax & Wording alert — only render when the NLP
                            analyser has actually flagged issues on a stored
                            agreement. Previously it was hardcoded on, so it
                            kept warning even after the agreement had been
                            extracted, reviewed and saved. */}
                        {(property as any)?.agreementSyntaxIssuesCount > 0 && (
                            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg shadow-sm">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <i className="fas fa-file-contract text-amber-500"></i>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-bold text-amber-800">
                                            Level 2 Amber Alert: Syntax & Wording Deficiencies
                                        </h3>
                                        <div className="mt-2 text-sm text-amber-700">
                                            <p className="font-bold">Warning: Vulnerable clauses or syntax errors detected in your tenancy agreement that may invalidate notices.</p>
                                            <p className="mt-1 font-medium">Please review the NLP document analysis to identify and remediate risky clauses.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* F-307: the Litigation Readiness card is redundant on
                            Texas — its pillar logic now renders inside Texas
                            Insights (health passed through). UK unchanged. */}
                        <div className="lg:col-span-3">
                            {property?.health && !isTexasMarket && <HealthScoreWidget health={property.health} />}
                        </div>

                        {/* F-306/F-307: Texas Insights element (Analytics + Score
                            from the old 'Other' menu group) — lives on each
                            property card, rolls up into the dashboard summary,
                            and carries the Litigation pillars + doc score. */}
                        {isTexasMarket && id && (
                            <div className="lg:col-span-3">
                                <TexasPropertyInsights
                                    propertyId={id}
                                    health={(property as any)?.health}
                                    certificates={property?.certificates}
                                    yearBuilt={(property as any)?.yearBuilt}
                                />
                            </div>
                        )}

                        {/* Left Column: Details */}
                        <div className="lg:col-span-1 space-y-6">
                            {/* Current Tenants Section */}
                            <div className="bg-white shadow rounded-lg p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Current Tenant</h3>
                                    <button
                                        onClick={() => setShowAddTenantModal(true)}
                                        className="text-xs font-bold text-green-600 hover:text-green-800 flex items-center gap-1"
                                    >
                                        <i className="fas fa-plus"></i> ADD TENANT
                                    </button>
                                </div>

                                {tenants.length > 0 ? (
                                    <div className="space-y-3">
                                        {[...tenants].sort((a, b) => Number(isActiveTenancy(b)) - Number(isActiveTenancy(a))).map(tenant => {
                                            const ended = !isActiveTenancy(tenant);
                                            return (
                                            <div
                                                key={tenant.id}
                                                className={`p-3 rounded-xl border flex justify-between items-center group ${ended
                                                    ? 'bg-gray-100 border-gray-200 opacity-60'
                                                    : 'bg-gray-50 border-gray-100'
                                                }`}
                                            >
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className={`text-sm font-bold ${ended ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{tenant.name}</p>
                                                        {ended && (
                                                            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-300 text-gray-700">Ended</span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500">{tenant.email}</p>
                                                    <p className="text-[10px] text-gray-400 mt-1">
                                                        Started: {new Date(tenant.tenancyStartDate || Date.now()).toLocaleDateString()}
                                                        {ended && tenant.tenancyEndDate && <> &middot; Ended: {new Date(tenant.tenancyEndDate).toLocaleDateString()}</>}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedTenant(tenant);
                                                            setShowEditTenantModal(true);
                                                        }}
                                                        className="text-indigo-400 hover:text-indigo-600"
                                                    >
                                                        <i className="fas fa-edit text-xs"></i>
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (window.confirm(`Are you sure you want to remove ${tenant.name}?`)) {
                                                                try {
                                                                    await tenantService.deleteTenant(tenant.id);
                                                                    fetchData();
                                                                } catch (err) {
                                                                    alert('Failed to delete tenant');
                                                                }
                                                            }
                                                        }}
                                                        className="text-red-400 hover:text-red-600"
                                                    >
                                                        <i className="fas fa-trash text-xs"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-xl">
                                        <p className="text-sm text-gray-400">No active tenants</p>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white shadow rounded-lg p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Property Details</h3>
                                    <button
                                        onClick={() => setShowEditModal(true)}
                                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                    >
                                        <i className="fas fa-edit"></i> EDIT
                                    </button>
                                </div>
                                <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                    <div className="sm:col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Bedrooms</dt>
                                        <dd className="mt-1 text-sm text-gray-900 font-semibold">{property?.bedrooms || 'N/A'}</dd>
                                    </div>
                                    <div className="sm:col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Rent (Portfolio Yield)</dt>
                                        <dd className="mt-1 text-sm text-gray-900 font-bold">{isTexasMarket ? '$' : '£'}{totalRent || property?.rent || '0'}</dd>
                                    </div>
                                    <div className="sm:col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Tenant</dt>
                                        <dd className="mt-1 text-sm text-gray-900 font-semibold">{(() => {
                                            if (property?.type === 'HMO') {
                                                const count = tenants.filter((t: any) => t.status !== 'former' && t.status !== 'Inactive').length;
                                                if (count === 0) return 'Vacant';
                                                if (count < (property?.bedrooms || 1)) return 'Partially Tenanted';
                                                return 'Fully Tenanted';
                                            }
                                            return property?.tenantStatus || 'Vacant';
                                        })()}</dd>
                                    </div>
                                    <div className="sm:col-span-1">
                                        <dt className="text-sm font-medium text-gray-500">Type</dt>
                                        <dd className="mt-1 text-sm text-gray-900 font-semibold">{categoryLabel(property?.categoryPropertyType) || property?.type || 'Residential'}</dd>
                                    </div>
                                </dl>
                            </div>

                            {/* Comms History (v2.5.6) — every outbound message to a
                                tenant at this property. Backed by the MessageModel
                                so no more silent sends. Collapsible; renders below
                                Property Details / Current Tenant, which always stay
                                pinned to the top of this column. */}
                            {id && (
                                <CommsHistory
                                    propertyId={id}
                                    tenantNames={Object.fromEntries(tenants.map((t: any) => [t.id, t.name || t.email || t.id]))}
                                />
                            )}

                            {/* Rent Receipts (v2.5.6) — one card per active tenant.
                                Receipts are auto-generated + emailed on every
                                payment (webhook, manual, or resolved-pending). */}
                            {tenants.filter(isActiveTenancy).map((t: any) => (
                                <ReceiptsList key={`rcpt-${t.id}`} tenantId={t.id} tenantName={t.name || t.email || t.id} />
                            ))}

                            {/* F-306: Texas market replaces the UK PRS
                                Registration block with Rental Registrations +
                                7-Day Re-Key sections (human directive). */}
                            {isTexasMarket ? (
                                <>
                                    <RentalRegistrationCard propertyId={id || ''} />
                                    <RekeyStatusCard propertyId={id || ''} />
                                </>
                            ) : prsRegistrationVisible && (
                            <div className="bg-white shadow rounded-lg p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">
                                        <i className="fas fa-clipboard-list text-indigo-500 mr-2"></i>
                                        PRS Registration
                                    </h3>
                                    <button
                                        onClick={() => setShowEditModal(true)}
                                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                    >
                                        <i className="fas fa-edit"></i> EDIT
                                    </button>
                                </div>
                                {(() => {
                                    const regNumber = property?.prsRegistrationNumber;
                                    const expiry = property?.prsExpiryDate;
                                    const regDate = property?.prsRegistrationDate;

                                    // Calculate expiry warnings
                                    let expiryStatus: 'missing' | 'expired' | 'red' | 'amber' | 'ok' | 'no-expiry' = 'missing';
                                    if (regNumber && expiry) {
                                        const now = new Date();
                                        const expiryDate = new Date(expiry);
                                        const diffDays = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                        if (expiryDate < now) expiryStatus = 'expired';
                                        else if (diffDays <= 30) expiryStatus = 'red';
                                        else if (diffDays <= 60) expiryStatus = 'amber';
                                        else expiryStatus = 'ok';
                                    } else if (regNumber) {
                                        expiryStatus = 'no-expiry';
                                    }

                                    const statusBadge = (status: string) => {
                                        switch (status) {
                                            case 'missing': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-times-circle mr-1"></i>Not Registered</span>;
                                            case 'expired': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-exclamation-triangle mr-1"></i>Expired</span>;
                                            case 'red': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-clock mr-1"></i>Expiring Soon</span>;
                                            case 'amber': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800"><i className="fas fa-clock mr-1"></i>Due for Renewal</span>;
                                            case 'ok': return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-800"><i className="fas fa-check-circle mr-1"></i>Registered</span>;
                                            default: return null;
                                        }
                                    };

                                    return (
                                        <div>
                                            {!regNumber && (
                                                <div className="mb-3 bg-red-50 border-l-4 border-red-500 p-3 rounded-r">
                                                    <p className="text-sm font-bold text-red-800">PRS Registration Missing</p>
                                                    <p className="text-xs text-red-600">Register this property on the PRS Database after the October 2025 go-live.</p>
                                                </div>
                                            )}
                                            {expiryStatus === 'expired' && (
                                                <div className="mb-3 bg-red-50 border-l-4 border-red-500 p-3 rounded-r">
                                                    <p className="text-sm font-bold text-red-800">PRS Registration Expired</p>
                                                    <p className="text-xs text-red-600">Renewal is overdue. Renew immediately to stay compliant.</p>
                                                </div>
                                            )}
                                            {expiryStatus === 'red' && (
                                                <div className="mb-3 bg-red-50 border-l-4 border-red-500 p-3 rounded-r">
                                                    <p className="text-sm font-bold text-red-800">PRS Registration Expiring Within 30 Days</p>
                                                    <p className="text-xs text-red-600">Renewal is due soon. Please renew before <strong>{new Date(expiry!).toLocaleDateString('en-GB')}</strong>.</p>
                                                </div>
                                            )}
                                            {expiryStatus === 'amber' && (
                                                <div className="mb-3 bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r">
                                                    <p className="text-sm font-bold text-amber-800">PRS Registration Due for Renewal Within 60 Days</p>
                                                    <p className="text-xs text-amber-600">Renewal is due on <strong>{new Date(expiry!).toLocaleDateString('en-GB')}</strong>.</p>
                                                </div>
                                            )}
                                            <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                                                <div className="sm:col-span-2">
                                                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                                                    <dd className="mt-1">{statusBadge(expiryStatus)}</dd>
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <dt className="text-sm font-medium text-gray-500">Registration Number</dt>
                                                    <dd className="mt-1 text-sm text-gray-900 font-semibold">{regNumber || <span className="text-gray-400 italic">Not provided</span>}</dd>
                                                </div>
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Registration Date</dt>
                                                    <dd className="mt-1 text-sm text-gray-900 font-semibold">{regDate ? new Date(regDate).toLocaleDateString('en-GB') : <span className="text-gray-400 italic">—</span>}</dd>
                                                </div>
                                                <div>
                                                    <dt className="text-sm font-medium text-gray-500">Expiry Date</dt>
                                                    <dd className="mt-1 text-sm text-gray-900 font-semibold">{expiry ? new Date(expiry).toLocaleDateString('en-GB') : <span className="text-gray-400 italic">—</span>}</dd>
                                                </div>
                                            </dl>
                                        </div>
                                    );
                                })()}
                            </div>
                            )}
                        </div>

                        {/* Right Column: Certificates & Evidence */}
                        <div className="lg:col-span-2 space-y-6">
                            <AcknowledgementList
                                propertyId={id || ''}
                                refreshTrigger={ackRefreshTrigger}
                            />

                            {/* F-306: Texas market replaces the UK certificate
                                tracker with the HVAC Readiness element. */}
                            {isTexasMarket ? (
                                <HvacReadinessCard propertyId={id || ''} />
                            ) : (
                            <div id="certificate-list">
                                <CertificateList
                                    propertyId={id || ''}
                                    certificates={property?.certificates || []}
                                    onRefresh={fetchProperty}
                                    hasGasInstallation={property?.gasInstallation !== false}
                                />
                            </div>
                            )}

                            {/* Evidence Vault (F-004) */}
                            <EvidenceVault propertyId={id || ''} />

                            {/* F-306/F-307: Texas market replaces the NLP
                                Compliance analysis with the digital-vault
                                document validation (5 editable slots + inline
                                upload, score reported in Texas Insights). */}
                            {isTexasMarket ? (
                                <TexasDocumentValidation
                                    propertyId={id || ''}
                                    certificates={property?.certificates || []}
                                    yearBuilt={(property as any)?.yearBuilt}
                                    onUploaded={fetchProperty}
                                />
                            ) : (
                            <NLPAnalysisPanel
                                s3Key={property?.certificates?.find((c: any) =>
                                    c.type === 'Tenancy Agreement' ||
                                    c.type === 'tenancy_agreement'
                                )?.storageKey}
                                documentText={property?.certificates?.find((c: any) =>
                                    c.type === 'Tenancy Agreement' ||
                                    c.type === 'tenancy_agreement'
                                )?.documentText}
                                onAnalysisComplete={(res) => {
                                    console.log('[PropertyDetail] NLP Analysis finished:', res);
                                    // Optionally update property health/score here if not already done by backend
                                }}
                            />
                            )}
                        </div>
                    </div>

                </div>

                {/* F-009 Acknowledgement Modal */}
                <RequestAcknowledgementModal
                    isOpen={showAckModal}
                    onClose={() => setShowAckModal(false)}
                    propertyId={id || ''}
                    propertyAddress={typeof property?.address === 'object' ? (property?.address?.line1 || 'Property') : (property?.address || 'Property')}
                    onSuccess={fetchProperty}
                    tenants={tenants}
                />

                <OrderServiceModal
                    isOpen={showOrderModal}
                    onClose={() => setShowOrderModal(false)}
                    propertyId={id || ''}
                    propertyAddress={typeof property?.address === 'object' ? (property?.address?.line1 || 'Property') : (property?.address || 'Property')}
                    tenants={tenants}
                />

                {/* Critical Compliance Breach Modal */}
                <CriticalBreachModal
                    isOpen={showBreachModal}
                    onClose={() => setShowBreachModal(false)}
                    missingTypes={property?.hasGasInstallation === false
                        ? (property?.missingCriticalDocs || []).filter((t: string) => t !== 'gas_safety')
                        : (property?.missingCriticalDocs || [])
                    }
                    propertyId={id}
                />

                <EditPropertyModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    property={property}
                    onSuccess={fetchProperty}
                />

                <AddTenantModal
                    isOpen={showAddTenantModal}
                    onClose={() => setShowAddTenantModal(false)}
                    propertyId={id || ''}
                    onSuccess={fetchData}
                />

                <EditTenantModal
                    isOpen={showEditTenantModal}
                    onClose={() => setShowEditTenantModal(false)}
                    tenant={selectedTenant}
                    onSuccess={fetchData}
                />

                {!isDrawer && <Footer />}
            </div>
        );
}
